import React from 'react'
import { Paragraph, useScrollToSection } from './paragraph'
import { CardFullHeightScrollY } from '../../card/card-full-height-scroll-y'

// Our privacy policy, written in plain words from what the platform does (it replaced the
// 2024 text in November 2026). Keep it true whenever something changes what we collect,
// who receives it or how long we keep it:
// - outside services are described by what they do ("our speech services"), never by
//   name. Who they are is kept in the workspace's internal provider list, for anyone who
//   asks and for the data processing agreement;
// - retention comes from the cleanup jobs (audit.settings defaults, consent-log-expire,
//   email-retention, the error tracker's request_days) and the backup settings;
// - the public website's cookie rules come from edxr-marketing src/lib/third-parties.json
//   (modes.optIn, and media starts off everywhere);
// - the admin's own cookies are listed on the cookie page beside this one (cookie.js).
// The public website's footer links straight to the notice (NOTICE_SECTION).

export const PRIVACY_POLICY_UPDATED = 'November 3, 2026'

export const NOTICE_SECTION = 'notice-at-collection'

const PRIVACY_EMAIL = 'privacy@educationxr.com'
const WEBSITE_COOKIES = 'https://educationxr.com/cookies'
const ADMIN_COOKIES = '/public/legal/cookie'

const Mail = () => <a href={`mailto:${PRIVACY_EMAIL}`}>{PRIVACY_EMAIL}</a>

const SECTIONS = [
  { id: NOTICE_SECTION, title: 'At a glance' },
  { id: 'organizations', title: 'Organizations and personal accounts' },
  { id: 'what-we-collect', title: 'What we collect' },
  { id: 'how-we-use-it', title: 'How we use it' },
  { id: 'who-we-share-it-with', title: 'Who we share it with' },
  { id: 'cookies', title: 'Cookies and browser privacy signals' },
  { id: 'how-long-we-keep-it', title: 'How long we keep it' },
  { id: 'your-rights', title: 'Your rights' },
  { id: 'security', title: 'Security' },
  { id: 'where-it-is-stored', title: 'Where information is stored' },
  { id: 'children', title: 'Children' },
  { id: 'changes', title: 'Changes to this policy' },
  { id: 'contact', title: 'Contact' }
]

// The notice at collection: what we collect, why, and for how long.
const AT_A_GLANCE = [
  ['Your account: name, email address, organizations and roles', 'To provide EducationXR and sign you in', 'While the account exists'],
  [
    'Sign-ins and requests: IP address, approximate location (city), app and device',
    'To keep accounts secure and show you where you signed in',
    '90 days'
  ],
  [
    'Learning activity: courses, progress, results, assessments and surveys',
    'To provide courses and show your organization its members’ progress',
    'While your organization keeps them'
  ],
  ['What you create: courses, files and documents', 'To provide EducationXR', 'Until you or your organization delete them'],
  ['Voice and AI: what you say to voice features and send to AI features', 'To answer you', 'We don’t keep recordings of your voice'],
  ['Crash and error reports: what went wrong, the app, the device and the account', 'To fix problems', '90 days'],
  [
    'Our website: analytics cookies where allowed, and a record of your cookie choice',
    'To see how the site is used, and to show what you chose',
    'Cookie choice records 12 months'
  ],
  [
    'Only if you choose to share it: how you use the AI tools in the Caffeine editor',
    'To improve the AI tools',
    'Until you stop sharing, then kept without your name'
  ]
]

// How long we keep it. Backups are covered below the table.
const RETENTION = [
  ['Account and organization information', 'For as long as the account or organization exists'],
  ['Learning progress and results', 'For as long as your organization keeps them, or until your account is deleted'],
  ['Records of requests to our servers (IP address, location, app, device)', '90 days'],
  ['The history of changes (who changed what)', '12 months'],
  ['Course views', '90 days'],
  ['Course launches', '365 days'],
  ['Crash and error reports', '90 days. A short summary of each problem, with which accounts it affected, is kept to track the fix'],
  ['Records of the service emails we sent', '1 year; their delivery records 90 days'],
  ['Records of cookie choices on our website', '12 months'],
  ['How you use the AI tools, if you chose to share it', 'Until you stop sharing or delete your account, then kept without your name or organization']
]

const Table = ({ head, rows }) => (
  <div className="table-responsive">
    <table className="table table-sm align-top">
      <thead>
        <tr>
          {head.map(h => (
            <th key={h} scope="col">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map(row => (
          <tr key={row[0]}>
            {row.map((cell, i) => (
              <td key={i}>{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
)

export const Privacy = () => {
  useScrollToSection()
  return (
    <CardFullHeightScrollY title="privacy">
      <Paragraph header="PRIVACY POLICY">
        <p>
          EducationXR is provided by Heizenrader LLC (“we”, “us”). This policy explains what information we collect when you use
          EducationXR, how we use it, who we share it with and the choices you have. It covers our public website (educationxr.com), the
          admin website, the desktop, mobile and headset apps, the web player, and the Caffeine editor and its AI tools. Last updated{' '}
          {PRIVACY_POLICY_UPDATED}.
        </p>
        <ul className="list-unstyled">
          {SECTIONS.map(s => (
            <li key={s.id}>
              <a href={`#${s.id}`}>{s.title}</a>
            </li>
          ))}
        </ul>
      </Paragraph>

      <Paragraph header="AT A GLANCE: WHAT WE COLLECT AND WHY" id={NOTICE_SECTION}>
        <p>This is our notice at collection: what we collect, why, and for how long. The sections below give the details.</p>
        <Table head={['What we collect', 'Why', 'How long we keep it']} rows={AT_A_GLANCE} />
        <p>
          We don’t sell personal information, and we don’t share it for targeted advertising. We don’t use it to work out sensitive
          things about you. To ask for a copy of your information, or to have it corrected or deleted, see{' '}
          <a href="#your-rights">Your rights</a>.
        </p>
      </Paragraph>

      <Paragraph header="ORGANIZATIONS AND PERSONAL ACCOUNTS" id="organizations">
        <p>
          Most people use EducationXR through an organization, such as an employer or a training provider. The organization decides how
          its members’ information is used, and we handle that information for it under our agreement with it. If you use EducationXR
          through an organization, its own privacy notice applies too, and some requests, such as deleting your learning records, may
          need to go through it. We’ll help it answer.
        </p>
        <p>For personal accounts and for our public website, we decide how information is used, and this policy covers that use in full.</p>
      </Paragraph>

      <Paragraph header="WHAT WE COLLECT" id="what-we-collect">
        <p>
          <strong>Your account.</strong> Your name, email address and password. The password is kept only as a one-way scrambled value,
          never as you typed it. We also keep the organizations you belong to and your role in each, invitations you send or receive,
          your settings, and the choices you make about optional sharing.
        </p>
        <p>
          <strong>Signing in and security.</strong> When and how you signed in, your active sessions and the devices you approved. Each
          request to our servers is recorded: when it was made, the IP address and app it came from, who made it, and what was asked,
          with passwords and other secrets removed. We work out an approximate location (the city) from the IP address on our own
          servers, without asking any outside service. We use it to protect accounts and to show you where a sign-in came from.
        </p>
        <p>
          <strong>Your device and app.</strong> The app and its version, your operating system, and your device’s type, model and name.
          In the web player, also your browser, language and screen size. When you run a course, also your device’s graphics, processor
          and memory, and your headset’s model, so we can find and fix performance problems. For the Caffeine editor, also the editor’s
          version.
        </p>
        <p>
          <strong>Crash and error reports.</strong> When an app or our admin website hits an error, it sends us a report: what went wrong,
          where, and on which app and device. A report sent while you are signed in is linked to your account and organization. The
          desktop and mobile apps also send crash reports, with your email address, your account and organization and the course you
          were in, and basic app usage, such as when the app starts, to our app engine’s services.
        </p>
        <p>
          <strong>Learning activity.</strong> The courses you open and launch, the steps and objectives you complete and how long you
          spend, the attachments you view, your answers and results in assessments and surveys, and whether you took part in a
          multiplayer session.
        </p>
        <p>
          <strong>Multiplayer sessions.</strong> While you are in a multiplayer session, the others in it see your name, and we keep the
          room and who is in it until the session ends. Voice chat is passed live between the people in the session through our
          multiplayer services. We don’t record it.
        </p>
        <p>
          <strong>What you create.</strong> Courses, files, images and other content you upload, and documents you add to AI features.
        </p>
        <p>
          <strong>Voice.</strong> When you speak to a voice feature, such as an AI character or a voice command, your speech goes from
          your device to our speech services to be turned into text, and their spoken replies are made by the same services. We don’t
          keep recordings of your voice.
        </p>
        <p>
          <strong>AI features.</strong> The questions and messages you send to AI features go to our AI services, with the conversation so
          far and the parts of the course’s documents needed to answer. So do the answers they write. Documents added to AI features are prepared for searching with the help of the same services, and the
          result is kept on our own servers. We don’t use your content or learning records to train AI models.
        </p>
        <p>
          <strong>Learning management systems.</strong> If your organization launches courses from its learning management system, we
          receive what that system sends to identify you: an ID, your role, and your email address if it shares it. We send your scores
          and progress back to it. If your organization connects a learning record store, we send your learning activity there.
        </p>
        <p>
          <strong>Web pages inside courses.</strong> Some courses show web pages chosen by the course’s author. Those pages come straight
          from their own sites, under those sites’ own policies.
        </p>
        <p>
          <strong>Messages to us.</strong> What you send when you contact support or use the contact form on our website: your name,
          email address, what you’re interested in and your message.
        </p>
        <p>
          <strong>Emails we send.</strong> Our service emails, such as invitations, sign-in links and account notices, contain a small
          image that tells us whether the email was opened. We also keep a record of each email we sent and whether it was delivered.
        </p>
        <p>
          <strong>Optional: how you use the AI tools in the Caffeine editor.</strong> Only if you choose to share it, the editor’s AI tools
          send us a record of how they were used: which tools ran, what they were asked to do, whether it worked, how long it took, and
          any problems you report. It is linked to your account and organization. You can see what is shared, and stop at any time.
          Stopping applies on every computer you use, and what was already sent is then kept without your name or organization.
        </p>
        <p>
          <strong>Our website.</strong> Our public website uses analytics cookies, can show embedded videos and slides, and keeps a record
          of each cookie choice. See <a href="#cookies">Cookies and browser privacy signals</a>.
        </p>
      </Paragraph>

      <Paragraph header="HOW WE USE IT" id="how-we-use-it">
        <ul>
          <li>
            To provide EducationXR: signing you in, delivering courses, running multiplayer sessions, AI and voice features, and showing
            your organization its members’ progress and results.
          </li>
          <li>To keep accounts and the service secure: spotting unusual sign-ins, limiting repeated attempts and recording who changed what.</li>
          <li>To apply your organization’s plan, seats and limits.</li>
          <li>To support you, and to send service emails such as invitations, sign-in links, password resets and account notices.</li>
          <li>To find and fix problems, and to understand how EducationXR is used so we can improve it.</li>
          <li>To meet legal obligations.</li>
        </ul>
        <p>We don’t sell personal information, and we don’t share it for targeted advertising.</p>
        <p>
          <strong>Legal bases (EU, UK and similar laws).</strong> We rely on the contract with you or your organization to provide the
          service; on our legitimate interests for security, fixing problems and improving EducationXR; on your consent for website
          analytics, embedded videos and slides, and sharing how you use the AI tools; and on legal obligations, for example keeping
          records the law requires.
        </p>
      </Paragraph>

      <Paragraph header="WHO WE SHARE IT WITH" id="who-we-share-it-with">
        <ul>
          <li>
            <strong>Your organization.</strong> Its owners and administrators can see members’ activity, progress and results, according
            to the roles they give.
          </li>
          <li>
            <strong>Service providers who run parts of EducationXR for us.</strong> They may use the information only to provide their
            service to us, and must protect it:
            <ul>
              <li>hosting and storage services;</li>
              <li>email services;</li>
              <li>multiplayer and voice chat services;</li>
              <li>speech services;</li>
              <li>AI services;</li>
              <li>our app engine’s crash-reporting and usage services, for the desktop and mobile apps;</li>
              <li>
                map services, which receive the approximate location of a sign-in (the centre of the city) to draw a map when you approve
                a device;
              </li>
              <li>a security service that checks that the event sign-up form is filled in by a person;</li>
              <li>website analytics services, as described under Cookies;</li>
              <li>video and slide services, when you play an embedded video or slides on our website.</li>
            </ul>
          </li>
          <li>
            <strong>Your organization’s learning systems,</strong> when it connects them: its learning management system and learning
            record store.
          </li>
          <li>
            <strong>Sign-in providers you choose,</strong> such as your organization’s single sign-on or another account you sign in with.
          </li>
          <li>
            <strong>App stores,</strong> when you download our apps. The store handles that under its own policy.
          </li>
          <li>
            <strong>Advisers and authorities,</strong> when the law requires it, or to protect people, our users or EducationXR.
          </li>
          <li>
            <strong>A buyer or successor,</strong> if our business is sold or merged. This policy keeps applying to your information.
          </li>
        </ul>
        <p>
          You can ask us for the names of the service providers that handle your information: <Mail />.
        </p>
      </Paragraph>

      <Paragraph header="COOKIES AND BROWSER PRIVACY SIGNALS" id="cookies">
        <p>
          <strong>Our public website</strong> uses analytics cookies to count visits, and can show embedded videos and slides that set
          their own cookies.
        </p>
        <ul>
          <li>
            Where the law asks for consent first, for example in the EU, the UK, Brazil, California and Quebec, and wherever we can’t tell
            where you are, nothing optional runs until you choose.
          </li>
          <li>Elsewhere, analytics are on unless you turn them off.</li>
          <li>
            Embedded videos and slides wait everywhere until you choose to play them. Each one says which service it loads from first.
          </li>
          <li>
            Your choice is remembered for 6 months, then we ask again. Change it any time from “Cookie settings” at the bottom of every
            page.
          </li>
        </ul>
        <p>
          <strong>Our record of your choice.</strong> We keep a record of each cookie choice made on our website: when it was made, what
          was chosen and which button was used, the banner shown, which rules applied and why, whether your browser sent a privacy
          signal, the page, the browser, the country and state or province, a shortened IP address and a consent ID. We keep each record
          for 12 months, to show what was chosen. Your consent ID is in Cookie settings; if you ask us about your choice, it lets us find
          your record. The full list of the website’s cookies is at{' '}
          <a href={WEBSITE_COOKIES} target="_blank" rel="noopener noreferrer">
            educationxr.com/cookies
          </a>
          .
        </p>
        <p>
          <strong>The admin website and the web player</strong> keep your sign-in and settings in your browser, and copies of course
          pictures so they load faster. These are needed for the service to work. The full list is in our{' '}
          <a href={ADMIN_COOKIES}>cookie policy</a>. Our documentation site uses a short sign-in cookie, and the event sign-up form’s
          security check may set its own cookie.
        </p>
        <p>Our apps don’t use advertising cookies or advertising identifiers.</p>
        <p>
          <strong>Global Privacy Control.</strong> If your browser sends this signal, our public website treats it as your choice to keep
          optional cookies off, wherever you are, and Cookie settings shows “Opt-out preference signal honored”.
        </p>
        <p>
          <strong>Do Not Track.</strong> Our websites don’t respond to Do Not Track. The standard was never finished, and browsers have
          moved to Global Privacy Control instead. The admin website and the web player use only what they need to work, so there is
          nothing for either signal to switch off.
        </p>
      </Paragraph>

      <Paragraph header="HOW LONG WE KEEP IT" id="how-long-we-keep-it">
        <Table head={['Information', 'How long']} rows={RETENTION} />
        <p>
          These periods can differ where your organization’s agreement sets another period, or where the law requires us to keep
          something longer. Deleted information stays in our backups for up to 35 days, and in our disaster-recovery copies of files for
          up to 180 days, before it is removed.
        </p>
        <p>
          <strong>When you delete your account:</strong> your personal organization and its content are deleted. In other organizations
          your membership ends and the content stays with the organization, and your learning records there are kept without your name,
          so the organization’s reports stay complete.
        </p>
      </Paragraph>

      <Paragraph header="YOUR RIGHTS" id="your-rights">
        <p>Depending on where you live, you can ask us to:</p>
        <ul>
          <li>give you a copy of your information, or export it;</li>
          <li>correct it;</li>
          <li>delete it;</li>
          <li>restrict how we use it, or object to it;</li>
          <li>withdraw consent you gave, at any time.</li>
        </ul>
        <p>
          Email <Mail />. We’ll confirm who you are before acting, and we won’t treat you differently for using these rights. If your
          account is managed by an organization, we may pass the request to it.
        </p>
        <p>
          <strong>EU, UK and similar laws:</strong> you can also complain to your local data protection authority.
        </p>
      </Paragraph>

      <Paragraph header="SECURITY" id="security">
        <ul>
          <li>
            Information is encrypted on its way between your device and our servers. Our main database, file storage and caches are
            also encrypted where they are stored.
          </li>
          <li>Access is limited to staff who need it, and staff actions are recorded, including when staff act on someone’s behalf.</li>
          <li>Sign-ins are protected by limits on repeated attempts and by device approval.</li>
        </ul>
        <p>No system is perfectly secure. If a breach affects your information, we’ll tell you and the authorities as the law requires.</p>
      </Paragraph>

      <Paragraph header="WHERE INFORMATION IS STORED" id="where-it-is-stored">
        <p>
          EducationXR stores information in the United States. Where the law requires a safeguard for information sent to us from the
          EU, the UK, Switzerland or elsewhere, we use one, such as standard contractual clauses.
        </p>
      </Paragraph>

      <Paragraph header="CHILDREN" id="children">
        <p>
          EducationXR is not meant for children under 13, and we don’t knowingly collect their information. If we learn that we have, we
          delete it. Parents or guardians who believe a child under 13 has given us information can contact us at <Mail />.
        </p>
      </Paragraph>

      <Paragraph header="CHANGES TO THIS POLICY" id="changes">
        <p>
          We’ll post any change here and update the date at the top. Before a significant change takes effect, we’ll tell account holders
          by email or in EducationXR.
        </p>
      </Paragraph>

      <Paragraph header="CONTACT" id="contact">
        <p>
          Heizenrader LLC, <Mail />
        </p>
      </Paragraph>
    </CardFullHeightScrollY>
  )
}
